// The Journal's windows (chat C24, D06 part 1):
//
// `MovementDetailsModal` — extracted from original/src/components/MovementDetailsModal.tsx:462-1258 (ADR-08 §2.1 "copy,
// then cut logic"): the overlay and frame (:462-477), the header with the type badge, "#" code, date and time
// (:478-552), the top banner with the original description, note, account-impact line, currency badge and total value
// (:567-638), the badges bar (box, operator, commission, deferred) (:640-674), the "original parameters" card (:678-947,
// as label / value rows), the customer card with the account number and current balances (:949-1052), the ledger
// impact cards (:1137-1194) and the footer (:1196-1258). Kept: every class string, icon and size.
// Cut: the type detection over item text (`isCurrencyTrade`, `isCashTransfer`, `matchedTransfer` by code text, the
// "فرقوات" / time-window matching, :56-460 — D06 R-3, D-091) → `view` built by the container from the ledger
// operation and its lines; `formatNumber` / `toLocale…` → preformatted labels; the customer receipt voucher view
// (:520-565, `CustomerReceiptVoucher`) → the customer statement prints of C22; the multi-item sales list (:1054-1135,
// store add-on after G1p); `html2canvas` / `jsPDF` share / save (:1196-1240) → `onPrint` (the PDF worker is B5).
// Added: a history card listing the original / cancel / edit entries of the operation (D-013 "every edit is visible";
// V-42) and data-testid attributes.
//
// `MovementEditModal` — the frame of the old edit window (App.tsx:50961-53957): overlay, header "تعديل الحركة الذكي"
// with the id line, the movement-category banner (:51128-51221), the item and operator fields (:51222-51280, both
// read-only now), the submit / cancel buttons (:53920-53953). Cut by decision (D-092): the free-form fields over the
// stored movement (item text, deferred flag, in / out, cost, sale price, commission, digital commission / profit,
// type switch — D06 R-6, harness X2) → `fields`: only the business inputs of the original screen, listed by the
// server; the simulated balance (:50998-51043) → the server's before / after figures; a written reason and the PIN
// (D-018) are new (V-43).
//
// `JournalCancelDialog` (D-091, D-094) and `NegativeApprovalDialog` (D-014) are new dialogs in the style of the C23
// cancel dialog (V-44).
import { AnimatePresence, motion } from 'motion/react';
import {
  AlertTriangle, ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Calendar, Clock, Coins, DollarSign, Download, Edit2, FileText, History, Layers, Percent, Printer,
  SendHorizontal, Share2, ShieldCheck, Sparkles, TrendingUp, User, Wallet, X,
} from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '../i18n-context.js';

// ---------------------------------------------------------------------------------------------------------------------
// Details window
// ---------------------------------------------------------------------------------------------------------------------

export interface MovementDetailsView {
  readonly typeTitle: string;
  readonly badgeColor: string;
  readonly icon: 'coins' | 'transfer' | 'file';
  readonly code: string;
  readonly dateLabel: string;
  readonly timeLabel: string;
  readonly item: string;
  readonly note: string | null;
  readonly impactText: string | null;
  /** "يورو (EUR)" when the operation trades or moves a foreign currency. */
  readonly currencyBadge: string | null;
  readonly total: { readonly label: string; readonly direction: 'in' | 'out' | null };
  readonly boxName: string;
  readonly operator: string;
  readonly commissionLabel: string | null;
  readonly deferred: boolean;
  readonly params: ReadonlyArray<{ readonly label: string; readonly value: string; readonly tone?: 'in' | 'out' | 'amber' }>;
  readonly party: { readonly name: string; readonly kind: 'debt' | 'trust'; readonly accountNumber: string | null; readonly iqdLabel: string | null; readonly usdLabel: string | null } | null;
  readonly impact: { readonly inLabel: string; readonly outLabel: string; readonly commLabel: string; readonly fxTitle: string; readonly fxLabel: string };
  readonly history: ReadonlyArray<{ readonly label: string; readonly at: string; readonly actor: string; readonly reason: string | null }>;
  readonly statusBadge: string | null;
}

export interface MovementDetailsModalProps {
  readonly open: boolean;
  readonly view: MovementDetailsView | null;
  readonly canEdit: boolean;
  readonly onEdit: () => void;
  readonly onPrint: () => void;
  readonly onClose: () => void;
}

export function MovementDetailsModal(p: MovementDetailsModalProps) {
  const { t, locale } = useI18n();
  const isAr = locale === 'ar';
  const v = p.view;
  return (
    <AnimatePresence>
      {p.open && v && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="journal-details">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="fixed inset-0 bg-black/65 backdrop-blur-sm transition-opacity" />
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 15 }}
            className="relative bg-white dark:bg-[#0d1117] w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-[#30363d] flex flex-col max-h-[92vh] z-10"
            dir={isAr ? 'rtl' : 'ltr'}
          >
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-[#30363d] bg-gradient-to-r from-gray-50/80 via-white to-gray-50/80 dark:from-[#161b22] dark:via-[#0d1117] dark:to-[#161b22] flex flex-wrap items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3.5">
                <div className="p-3 bg-blue-600/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-2xl border border-blue-500/20 shadow-inner flex items-center justify-center">
                  {v.icon === 'coins' ? <Coins size={24} className="stroke-[2]" /> : v.icon === 'transfer' ? <SendHorizontal size={24} className="stroke-[2]" /> : <FileText size={24} className="stroke-[2]" />}
                </div>
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="text-lg sm:text-xl font-black text-gray-900 dark:text-[#f0f6fc] tracking-tight">{t('jr.details_title')}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border ${v.badgeColor}`}>{v.typeTitle}</span>
                    <span className="font-mono text-xs font-black px-2.5 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">#{v.code}</span>
                    {v.statusBadge && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-black border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800" data-testid="journal-details-status">
                        {v.statusBadge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={13} className="text-gray-400" />
                      {v.dateLabel}
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Clock size={13} className="text-gray-400" />
                      {v.timeLabel}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={p.onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] rounded-xl hover:bg-gray-100 dark:hover:bg-[#161b22] transition-colors" title={t('jr.close')}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6 custom-scrollbar bg-[#f8fafc]/50 dark:bg-[#05070a]">
              <div id="printable-movement-details" className="space-y-6">
                {/* Top Highlight Banner */}
                <div className="bg-gradient-to-br from-white to-gray-50 dark:from-[#0d1117] dark:to-[#161b22] p-5 sm:p-6 rounded-3xl border border-gray-200/80 dark:border-[#30363d] shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-blue-600 dark:bg-blue-500" />
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="space-y-1.5 max-w-xl">
                      <span className="text-[11px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                        <Sparkles size={12} className="text-amber-500" />
                        {t('jr.dt_original_desc')}
                      </span>
                      <h4 className="text-base sm:text-lg font-black text-gray-900 dark:text-white leading-snug" data-testid="journal-details-item">{v.item}</h4>
                      {v.note && (
                        <p className="text-xs text-blue-700 dark:text-blue-300 font-medium mt-1 bg-blue-50/70 dark:bg-blue-950/30 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/40 leading-relaxed">
                          <span className="font-bold">{t('jr.dt_note')}</span>
                          {v.note}
                        </p>
                      )}
                      {v.impactText && (
                        <div className="flex items-center gap-2 text-xs text-emerald-800 dark:text-emerald-300 bg-emerald-50/80 dark:bg-emerald-950/30 p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-900/40">
                          <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
                          <span className="font-semibold">{v.impactText}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      {v.currencyBadge && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 font-black text-xs">
                          <Coins size={14} className="text-amber-500" />
                          <span>{t('jr.dt_currency')}</span>
                          <span className="font-bold">{v.currencyBadge}</span>
                        </div>
                      )}
                      <span className="text-[11px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('jr.dt_total_value')}</span>
                      <div className="text-2xl sm:text-3xl font-black font-mono text-gray-900 dark:text-[#f0f6fc] tracking-tight">
                        {v.total.direction === 'out' ? (
                          <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1">
                            <ArrowUpRight size={24} />
                            {v.total.label}
                          </span>
                        ) : v.total.direction === 'in' ? (
                          <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <ArrowDownLeft size={24} />
                            {v.total.label}
                          </span>
                        ) : (
                          <span>{v.total.label}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Badges Bar */}
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[#30363d] flex flex-wrap items-center gap-3 text-xs font-bold">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300">
                      <Wallet size={14} className="text-blue-500" />
                      <span>{t('jr.dt_vault')}</span>
                      <span className="font-black text-gray-900 dark:text-white">{v.boxName}</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300">
                      <User size={14} className="text-emerald-500" />
                      <span>{t('jr.dt_operator')}</span>
                      <span className="font-black text-gray-900 dark:text-white">{v.operator}</span>
                    </div>
                    {v.commissionLabel && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/50 dark:border-blue-800/50">
                        <Percent size={14} className="text-blue-600" />
                        <span>{t('jr.dt_commission')}</span>
                        <span className="font-black font-mono">{v.commissionLabel}</span>
                      </div>
                    )}
                    {v.deferred && (
                      <span className="px-2.5 py-1 rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700 font-black">{t('jr.dt_deferred')}</span>
                    )}
                  </div>
                </div>

                {/* Original Operation Interface Details Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* 1. Transaction Parameters Card */}
                  <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-200/80 dark:border-[#30363d] shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-[#30363d] pb-3">
                      <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2">
                        <Layers size={16} className="text-blue-600 dark:text-blue-400" />
                        {t('jr.dt_params')}
                      </h4>
                      <span className="text-[11px] font-bold text-gray-400">{v.typeTitle}</span>
                    </div>
                    <div className="space-y-2.5 text-xs">
                      {v.params.map((r, i) => (
                        <div key={`${i}-${r.label}`} className="flex justify-between items-center py-1.5 border-b border-gray-50 dark:border-gray-800/60">
                          <span className="text-gray-500 dark:text-gray-400">{r.label}</span>
                          <span className={`font-black ${r.tone === 'in' ? 'text-emerald-600 dark:text-emerald-400' : r.tone === 'out' ? 'text-rose-600 dark:text-rose-400' : r.tone === 'amber' ? 'font-mono text-amber-600' : 'text-gray-800 dark:text-gray-200'}`}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 2. Customer & Partner Information Card */}
                  <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-200/80 dark:border-[#30363d] shadow-sm space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-[#30363d] pb-3">
                      <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2">
                        <User size={16} className="text-emerald-600 dark:text-emerald-400" />
                        {t('jr.dt_customer_card')}
                      </h4>
                      {v.party && (
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black ${v.party.kind === 'trust' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'}`}>
                          {v.party.kind === 'trust' ? t('jr.dt_trust_account') : t('jr.dt_debt_account')}
                        </span>
                      )}
                    </div>
                    {v.party ? (
                      <div className="space-y-2.5 text-xs">
                        <div className="flex justify-between items-center py-1.5 border-b border-gray-50 dark:border-gray-800/60">
                          <span className="text-gray-500 dark:text-gray-400">{t('jr.dt_customer_name')}</span>
                          <span className="font-black text-gray-900 dark:text-white text-sm">{v.party.name}</span>
                        </div>
                        {v.party.accountNumber && (
                          <div className="flex justify-between items-center py-1.5 border-b border-gray-50 dark:border-gray-800/60">
                            <span className="text-gray-500 dark:text-gray-400">{t('jr.dt_account_number')}</span>
                            <span className="font-black font-mono text-gray-800 dark:text-gray-200">{v.party.accountNumber}</span>
                          </div>
                        )}
                        {v.party.iqdLabel !== null && (
                          <div className="p-3.5 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700/50 space-y-2 mt-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black uppercase text-gray-400">{t('jr.dt_current_balance')}</span>
                              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${v.party.kind === 'trust' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300'}`}>
                                {v.party.kind === 'trust' ? t('jr.dt_trust_payable') : t('jr.dt_debt_receivable')}
                              </span>
                            </div>
                            <div className="flex justify-between items-center font-mono">
                              <span className="text-xs text-gray-600 dark:text-gray-400">{t('jr.dt_iqd_balance')}</span>
                              <span className="font-black text-sm text-gray-900 dark:text-white">{v.party.iqdLabel} د.ع</span>
                            </div>
                            {v.party.usdLabel && (
                              <div className="flex justify-between items-center font-mono">
                                <span className="text-xs text-gray-600 dark:text-gray-400">{t('jr.dt_usd_balance')}</span>
                                <span className="font-black text-sm text-emerald-600 dark:text-emerald-400">{v.party.usdLabel} $</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-400 dark:text-gray-500 text-xs">
                        <User size={32} className="mx-auto mb-2 opacity-30" />
                        <p>{t('jr.dt_no_customer')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 3. Ledger Impact Cards */}
                <div className="bg-white dark:bg-[#0d1117] p-5 sm:p-6 rounded-3xl border border-gray-200/80 dark:border-[#30363d] shadow-sm space-y-4">
                  <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2">
                    <TrendingUp size={16} className="text-indigo-600 dark:text-indigo-400" />
                    {t('jr.dt_impact')}
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                    <div className="p-3.5 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40">
                      <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-400 block mb-1">{t('jr.dt_cash_in')}</span>
                      <span className="text-base sm:text-lg font-black font-mono text-emerald-700 dark:text-emerald-300">{v.impact.inLabel}</span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-rose-50/70 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/40">
                      <span className="text-[10px] font-black uppercase tracking-wider text-rose-700 dark:text-rose-400 block mb-1">{t('jr.dt_cash_out')}</span>
                      <span className="text-base sm:text-lg font-black font-mono text-rose-700 dark:text-rose-300">{v.impact.outLabel}</span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-blue-50/70 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40">
                      <span className="text-[10px] font-black uppercase tracking-wider text-blue-700 dark:text-blue-400 block mb-1">{t('jr.dt_net_comm')}</span>
                      <span className="text-base sm:text-lg font-black font-mono text-blue-700 dark:text-blue-300">{v.impact.commLabel}</span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/40">
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-400 block mb-1">{v.impact.fxTitle}</span>
                      <span className="text-base sm:text-lg font-black font-mono text-amber-700 dark:text-amber-300">{v.impact.fxLabel}</span>
                    </div>
                  </div>
                </div>

                {/* [C24] history of the operation: original, cancels, edits (D-013, V-42) */}
                {v.history.length > 1 && (
                  <div className="bg-white dark:bg-[#0d1117] p-5 sm:p-6 rounded-3xl border border-gray-200/80 dark:border-[#30363d] shadow-sm space-y-3" data-testid="journal-details-history">
                    <h4 className="text-sm font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2">
                      <History size={16} className="text-indigo-600 dark:text-indigo-400" />
                      {t('jr.dt_history')}
                    </h4>
                    <div className="space-y-2 text-xs">
                      {v.history.map((h, i) => (
                        <div key={i} className="flex flex-wrap justify-between items-center gap-2 py-1.5 border-b border-gray-50 dark:border-gray-800/60">
                          <span className="font-black text-gray-800 dark:text-gray-200">{h.label}</span>
                          <span className="text-gray-500 dark:text-gray-400">{h.reason ?? ''}</span>
                          <span className="font-mono text-gray-400">{h.at} — {h.actor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions Footer */}
            <div className="p-4 sm:p-5 border-t border-gray-100 dark:border-[#30363d] bg-gray-50/90 dark:bg-[#05070a] flex flex-wrap items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                  {t('jr.dt_doc')} #{v.code}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                {p.canEdit && (
                  <button
                    type="button"
                    onClick={p.onEdit}
                    className="px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-800/40 font-black text-xs transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer"
                    data-testid="journal-details-edit"
                  >
                    <Edit2 size={14} />
                    <span>{t('jr.dt_edit')}</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={p.onPrint}
                  className="px-3.5 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800/40 font-black text-xs transition-all flex items-center gap-1.5 active:scale-95 disabled:opacity-50 cursor-pointer"
                >
                  <Share2 size={14} />
                  <span>{t('jr.dt_share_pdf')}</span>
                </button>
                <button
                  type="button"
                  onClick={p.onPrint}
                  className="px-3.5 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-800/40 font-black text-xs transition-all flex items-center gap-1.5 active:scale-95 disabled:opacity-50 cursor-pointer"
                >
                  <Download size={14} />
                  <span>{t('jr.dt_save_pdf')}</span>
                </button>
                <button
                  type="button"
                  onClick={p.onPrint}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-black text-xs transition-all shadow-md shadow-blue-600/20 flex items-center gap-1.5 cursor-pointer"
                  data-testid="journal-details-print"
                >
                  <Printer size={14} />
                  <span>{t('jr.dt_print')}</span>
                </button>
                <button
                  type="button"
                  onClick={p.onClose}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-gray-300 font-bold text-xs hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  {t('jr.close')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Edit window
// ---------------------------------------------------------------------------------------------------------------------

export type EditCategory = 'transfer' | 'usd' | 'debt' | 'general';

export interface EditFieldView {
  readonly path: string;
  readonly kind: 'amount' | 'rate' | 'text' | 'date' | 'account';
  readonly label: string;
  readonly value: string;
  readonly options?: ReadonlyArray<{ readonly id: string; readonly name: string }>;
}

export interface EditPreviewView {
  readonly before: { readonly inLabel: string; readonly outLabel: string; readonly commLabel: string };
  readonly after: { readonly inLabel: string; readonly outLabel: string; readonly commLabel: string };
  /** Accounts the correction would take below zero (D-014: the Super Manager's approval is asked at confirmation). */
  readonly negative: readonly string[];
  readonly requiresApproval: boolean;
}

export interface MovementEditModalProps {
  readonly open: boolean;
  readonly idLabel: string;
  readonly category: EditCategory;
  readonly item: string;
  readonly operator: string;
  readonly fields: readonly EditFieldView[];
  readonly onFieldChange: (path: string, value: string) => void;
  readonly reason: string;
  readonly onReasonChange: (v: string) => void;
  readonly preview: EditPreviewView | null;
  readonly error: string | null;
  readonly busy: boolean;
  /** Before a preview: "معاينة"; after: "اعتماد وحفظ التعديلات" (PIN follows). */
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

const CATEGORY_TONE: Record<EditCategory, string> = {
  transfer: 'bg-indigo-50/50 dark:bg-indigo-950/10 border-indigo-100 dark:border-indigo-900/30 text-indigo-800 dark:text-indigo-400',
  usd: 'bg-cyan-50/50 dark:bg-cyan-950/10 border-cyan-100 dark:border-cyan-900/30 text-cyan-800 dark:text-cyan-400',
  debt: 'bg-purple-50/50 dark:bg-purple-950/10 border-purple-100 dark:border-purple-900/30 text-purple-800 dark:text-purple-400',
  general: 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-100 dark:border-amber-900/30 text-amber-800 dark:text-amber-400',
};
const INPUT = 'w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black transition-all text-sm';
const LABEL = 'block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest';

export function MovementEditModal(p: MovementEditModalProps) {
  const { t, locale } = useI18n();
  return (
    <AnimatePresence>
      {p.open && (
        <div key="edit-modal-overlay" className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="journal-edit">
          <motion.div key="edit-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div
            key="edit-modal-dialog"
            initial={{ scale: 0.95, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 15 }}
            className="relative bg-white dark:bg-[#0d1117] w-full rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border flex flex-col max-h-[90vh] max-w-lg"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center shrink-0 transition-colors bg-gray-50/50 dark:bg-[#05070a]">
              <div className="flex items-center gap-3">
                <div className="p-2 h-10 w-10 rounded-xl flex items-center justify-center transition-colors bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                  <Edit2 size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-gray-900 dark:text-[#f0f6fc] tracking-tight">{t('jr.ed_title')}</h3>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider">{t('jr.ed_id', { id: p.idLabel })}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={p.onClose}
                className="p-2 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] rounded-full transition-colors text-gray-400 dark:text-[#8b949e] hover:text-gray-600 dark:hover:text-[#f0f6fc]"
              >
                <X size={18} />
              </button>
            </div>
            {/* Main Body */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                p.onSubmit();
              }}
              className="p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar"
            >
              {/* Dynamic Category Badge/Banner */}
              <div className={`p-4 rounded-2xl flex items-center gap-3 border ${CATEGORY_TONE[p.category]}`}>
                <div className="p-2.5 rounded-xl bg-white dark:bg-[#0d1117] shadow-sm">
                  {p.category === 'transfer' && <ArrowLeftRight size={20} className="text-indigo-500" />}
                  {p.category === 'usd' && <DollarSign size={20} className="text-cyan-500" />}
                  {p.category === 'debt' && <User size={20} className="text-purple-500" />}
                  {p.category === 'general' && <Wallet size={20} className="text-amber-500" />}
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-black uppercase tracking-wider opacity-70">{t('jr.ed_category')}</p>
                  <h4 className="text-sm font-black text-gray-800 dark:text-gray-100 leading-tight">{t(`jr.ed_cat_${p.category}`)}</h4>
                </div>
              </div>
              {/* 1. Item Description (generated from the operation, D-092) */}
              <div>
                <label className={LABEL}>{t('jr.ed_item')}</label>
                <input type="text" disabled value={p.item} className="w-full bg-gray-100 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 dark:text-[#8b949e] cursor-not-allowed font-black text-sm" />
              </div>
              {/* 2. OPERATOR */}
              <div>
                <label className={LABEL}>{t('jr.ed_operator')}</label>
                <select disabled value={p.operator} className="w-full bg-gray-100 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 dark:text-[#8b949e] cursor-not-allowed font-black text-sm">
                  <option value={p.operator}>{p.operator}</option>
                </select>
              </div>
              {/* business inputs of the original screen (D-092) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {p.fields.map((f) => (
                  <div key={f.path} className={f.kind === 'text' || f.kind === 'account' ? 'sm:col-span-2' : ''}>
                    <label className={LABEL}>{f.label}</label>
                    {f.kind === 'account' ? (
                      <select value={f.value} onChange={(e) => p.onFieldChange(f.path, e.target.value)} className={INPUT} data-testid={`journal-edit-${f.path}`}>
                        {(f.options ?? []).map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={f.kind === 'date' ? 'date' : 'text'}
                        inputMode={f.kind === 'amount' || f.kind === 'rate' ? 'decimal' : undefined}
                        value={f.value}
                        onChange={(e) => p.onFieldChange(f.path, e.target.value)}
                        className={`${INPUT} ${f.kind === 'amount' || f.kind === 'rate' ? 'font-mono' : ''}`}
                        data-testid={`journal-edit-${f.path}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              {/* reason (D-092) */}
              <div>
                <label className={LABEL}>{t('jr.ed_reason')}</label>
                <textarea value={p.reason} onChange={(e) => p.onReasonChange(e.target.value)} rows={2} className={INPUT} data-testid="journal-edit-reason" />
              </div>
              {/* server figures before / after (replaces the old simulated balance) */}
              {p.preview && (
                <div className="p-4 rounded-2xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/10 space-y-2 text-xs" data-testid="journal-edit-preview">
                  <div className="grid grid-cols-4 gap-2 font-black text-gray-400 uppercase tracking-wider text-[10px]">
                    <span />
                    <span>{t('in')}</span>
                    <span>{t('out')}</span>
                    <span>{t('comm')}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 font-mono font-black">
                    <span className="font-sans text-gray-500">{t('jr.ed_before')}</span>
                    <span className="text-emerald-600">{p.preview.before.inLabel}</span>
                    <span className="text-rose-600">{p.preview.before.outLabel}</span>
                    <span className="text-blue-600">{p.preview.before.commLabel}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 font-mono font-black">
                    <span className="font-sans text-gray-500">{t('jr.ed_after')}</span>
                    <span className="text-emerald-600">{p.preview.after.inLabel}</span>
                    <span className="text-rose-600">{p.preview.after.outLabel}</span>
                    <span className="text-blue-600">{p.preview.after.commLabel}</span>
                  </div>
                  {p.preview.negative.length > 0 && <p className="text-rose-600 font-bold">{t('jr.ed_negative', { accounts: p.preview.negative.join('، ') })}</p>}
                  {p.preview.requiresApproval && <p className="text-amber-600 font-bold">{t('jr.ed_requires_approval')}</p>}
                </div>
              )}
              {p.error && (
                <p className="text-xs font-bold text-rose-600" data-testid="journal-edit-error">
                  {p.error}
                </p>
              )}
              {/* Submit Actions */}
              <div className="flex gap-3 pt-2 shrink-0">
                <button
                  type="submit"
                  disabled={p.busy || p.reason.trim() === ''}
                  className="flex-1 py-4 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl transition-all active:scale-[0.99] text-xs bg-gradient-to-r from-blue-600 to-indigo-600 shadow-blue-500/10 hover:shadow-blue-500/20 hover:brightness-110 disabled:opacity-50"
                  data-testid="journal-edit-submit"
                >
                  {p.preview ? t('jr.ed_save') : t('jr.ed_preview')}
                </button>
                <button
                  type="button"
                  onClick={p.onClose}
                  className="py-4 px-6 bg-gray-100 dark:bg-[#161b22] text-gray-700 dark:text-[#8b949e] rounded-2xl font-black uppercase tracking-widest hover:bg-gray-200 dark:hover:bg-[#30363d] transition-all text-xs"
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Cancel / bulk cancel (D-091, D-094) and the D-014 approval
// ---------------------------------------------------------------------------------------------------------------------

export interface JournalCancelDialogProps {
  readonly open: boolean;
  readonly count: number;
  /** "#SM-12" for one operation, null for a batch. */
  readonly codeLabel: string | null;
  /** D-094: the batch summary shown before confirmation (count, totals per currency). */
  readonly summary: ReadonlyArray<{ readonly label: string; readonly value: string }>;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}

export function JournalCancelDialog(p: JournalCancelDialogProps) {
  const { t, locale } = useI18n();
  const [reason, setReason] = useState('');
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="journal-cancel-dialog">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-rose-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center">
                <AlertTriangle size={36} className="text-rose-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-500">{p.count > 1 ? t('jr.cx_title_bulk', { n: p.count }) : t('jr.cx_title')}</h3>
                {p.codeLabel && <p className="text-[11px] font-mono font-black text-indigo-600">{p.codeLabel}</p>}
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">{t('jr.cx_body')}</p>
              </div>
            </div>
            {p.summary.length > 0 && (
              <div className="rounded-2xl bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] p-3 space-y-1.5 text-xs" data-testid="journal-cancel-summary">
                {p.summary.map((s) => (
                  <div key={s.label} className="flex justify-between font-bold">
                    <span className="text-gray-500 dark:text-gray-400">{s.label}</span>
                    <span className="font-mono font-black text-gray-800 dark:text-gray-100">{s.value}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <label className="block font-black text-slate-700 dark:text-slate-300 text-xs">{t('jr.cx_reason')}</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500 font-extrabold text-slate-800 dark:text-slate-100 text-xs"
                data-testid="journal-cancel-reason"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                disabled={p.busy || reason.trim() === ''}
                onClick={() => p.onConfirm(reason.trim())}
                className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer"
                data-testid="journal-cancel-confirm"
              >
                {t('jr.cx_confirm')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setReason('');
                  p.onClose();
                }}
                className="w-full py-3.5 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all text-center cursor-pointer"
              >
                {t('jr.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface NegativeApprovalDialogProps {
  readonly open: boolean;
  /** "محفظتي الرئيسية: -400,000" per account the correction would take below zero. */
  readonly accounts: readonly string[];
  /** The caller is the Super Manager: a confirmation, then their own PIN. */
  readonly selfApproval: boolean;
  readonly busy: boolean;
  readonly onConfirm: (approval: { username: string; pin: string } | null) => void;
  readonly onClose: () => void;
}

export function NegativeApprovalDialog(p: NegativeApprovalDialogProps) {
  const { t, locale } = useI18n();
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const ready = p.selfApproval || (username.trim() !== '' && /^\d{6}$/.test(pin));
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[125] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="journal-negative-dialog">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-amber-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-5"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center">
                <ShieldCheck size={36} className="text-amber-500" />
              </div>
              <h3 className="text-lg font-black text-amber-600">{t('jr.neg_title')}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed">{t('jr.neg_body')}</p>
            </div>
            <ul className="text-xs font-mono font-black text-rose-600 space-y-1">
              {p.accounts.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
            {!p.selfApproval && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>{t('jr.neg_username')}</label>
                  <input value={username} onChange={(e) => setUsername(e.target.value)} className={INPUT} data-testid="journal-negative-username" />
                </div>
                <div>
                  <label className={LABEL}>{t('jr.neg_pin')}</label>
                  <input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} className={`${INPUT} font-mono text-center`} data-testid="journal-negative-pin" />
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <button
                type="button"
                disabled={p.busy || !ready}
                onClick={() => p.onConfirm(p.selfApproval ? null : { username: username.trim(), pin })}
                className="w-full py-3.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 cursor-pointer"
                data-testid="journal-negative-confirm"
              >
                {t('jr.neg_confirm')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPin('');
                  p.onClose();
                }}
                className="w-full py-3.5 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all cursor-pointer"
              >
                {t('jr.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
